/**
 * AnalysisResults - Main component for displaying advanced analysis results
 *
 * Displays all analysis sections in a single scrollable page without tabs.
 * Supports two layout modes:
 * - standalone: Full-width display for dedicated analysis page
 * - sideBySide: Compact layout for displaying alongside chart dashboard
 */

import React, {useState} from "react";
import {Card, CardHeader, CardBody, Row, Col, Badge, Alert, Collapse, Button} from "reactstrap";
import {AnalysisResult} from "../../types/advancedAnalysis";
import {BackgroundColorContext} from "../../contexts/BackgroundColorContext";
import SystemAssessmentCard from "./SystemAssessmentCard";
import PerformanceScoreCard from "./PerformanceScoreCard";
import IssuesList from "./IssuesList";
import RecommendationsList from "./RecommendationsList";
import CorrelationFindings from "./CorrelationFindings";

export type AnalysisLayoutMode = "standalone" | "sideBySide";

interface AnalysisResultsProps {
  result: AnalysisResult;
  cacheAgeMinutes?: number | null;
  onReanalyze?: () => void;
  onClose?: () => void;
  layoutMode?: AnalysisLayoutMode;
  isDemo?: boolean;
}

const AnalysisResults: React.FC<AnalysisResultsProps> = ({
  result,
  cacheAgeMinutes,
  onReanalyze,
  onClose,
  layoutMode = "standalone",
  isDemo = false
}) => {
  // Count issues by priority
  const criticalCount = result.IssuesFound.filter((i) => i.Priority === "critical").length;

  // Determine if we're in compact mode
  const isCompact = layoutMode === "sideBySide";
  const containerClass = isCompact ? "analysis-results-compact" : "analysis-results-standalone";

  // Debug panel state
  const [showDebug, setShowDebug] = useState(false);

  return (
    <BackgroundColorContext.Consumer>
      {({color}) => (
        <div className={containerClass}>
          <Card className="mb-3">
            <CardHeader className="analysis-header">
              <Row className="align-items-center">
                <Col>
                  <h3 className="mb-0">Advanced Analysis</h3>
                </Col>
                <Col xs="auto">
                  {onReanalyze && !isDemo && (
                    <Button
                      color="primary"
                      data-color={color}
                      size="sm"
                      onClick={onReanalyze}
                      className="reanalyze-btn mr-2"
                    >
                      <i className="fa fa-sync-alt mr-1"></i>
                      Re-analyze
                    </Button>
                  )}
                  {onClose && (
                    <Button color="secondary" outline size="sm" onClick={onClose} aria-label="Close panel">
                      <i className="fa fa-times"></i>
                    </Button>
                  )}
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {/* Overview Section */}
              <section className="mb-4">
                <Row>
                  <Col md={isCompact ? "12" : "6"} className="mb-3">
                    <SystemAssessmentCard assessment={result.SystemAssessment} />
                  </Col>
                  <Col md={isCompact ? "12" : "6"} className="mb-3">
                    <PerformanceScoreCard score={result.PerformanceScore} />
                  </Col>
                </Row>

                {/* Critical Findings Alert */}
                {result.CriticalFindings && result.CriticalFindings.length > 0 && (
                  <Row className="mt-3">
                    <Col>
                      <div className="critical-findings-alert">
                        <h5 className="alert-heading">
                          <i className="fa fa-exclamation-triangle mr-2"></i>
                          Critical Findings
                        </h5>
                        <ul>
                          {result.CriticalFindings.map((finding, index) => (
                            <li key={index}>{finding}</li>
                          ))}
                        </ul>
                      </div>
                    </Col>
                  </Row>
                )}
              </section>

              {/* Correlation Findings Section - Show early if available */}
              {result.CorrelationFindings && result.CorrelationFindings.length > 0 && (
                <section className="mb-4">
                  <h4 className="analysis-section-header">
                    <i className="fa fa-project-diagram"></i>
                    Performance Insights
                    <Badge color="info">{result.CorrelationFindings.length}</Badge>
                  </h4>
                  <CorrelationFindings findings={result.CorrelationFindings} />
                </section>
              )}

              {/* Issues Section */}
              <section className="mb-4">
                <h4 className="analysis-section-header">
                  <i className="fa fa-exclamation-circle"></i>
                  Issues Detected
                  <Badge color="secondary">{result.IssuesFound.length}</Badge>
                  {criticalCount > 0 && (
                    <Badge color="danger" className="ml-2">
                      {criticalCount} Critical
                    </Badge>
                  )}
                </h4>
                {result.IssuesFound.length > 0 ? (
                  <IssuesList issues={result.IssuesFound} />
                ) : (
                  <Alert color="success" className="analysis-success-state">
                    <i className="fa fa-check-circle mr-2"></i>
                    No issues detected. Your system is performing well!
                  </Alert>
                )}
              </section>

              {/* Recommendations Section */}
              <section className="mb-4">
                <h4 className="analysis-section-header">
                  <i className="fa fa-lightbulb"></i>
                  Recommendations
                  <Badge color="secondary">{result.Recommendations.length}</Badge>
                </h4>
                {result.Recommendations.length > 0 ? (
                  <RecommendationsList recommendations={result.Recommendations} issues={result.IssuesFound} />
                ) : (
                  <Alert color="success" className="analysis-success-state">
                    <i className="fa fa-check-circle mr-2"></i>
                    No recommendations at this time. Your configuration appears optimal for the observed performance.
                  </Alert>
                )}
              </section>

              {/* Analysis Info Card - at bottom before debug */}
              <section className="mb-4">
                <Row>
                  <Col md="12">
                    <Card className="analysis-summary-card">
                      <CardBody>
                        <h5 className="card-title">
                          <i className="fa fa-info-circle"></i>
                          Analysis Info
                        </h5>
                        <ul className="summary-list">
                          <li>
                            <strong>Processed:</strong>
                            <span>{new Date(result.Timestamp).toLocaleString()}</span>
                          </li>
                          <li>
                            <strong>Processing Time:</strong>
                            <span>{(result.ProcessingTimeMs / 1000).toFixed(2)}s</span>
                          </li>
                          <li>
                            <strong>Language:</strong>
                            <span>{result.Language}</span>
                          </li>
                          <li>
                            <strong>Engine Version:</strong>
                            <span>{result.Version}</span>
                          </li>
                        </ul>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </section>

              {/* Debug Panel - moved to bottom */}
              <section className="mt-4">
                <Button color="info" data-color={color} size="sm" outline onClick={() => setShowDebug(!showDebug)}>
                  <i className={`fa fa-${showDebug ? "eye-slash" : "bug"} mr-1`}></i>
                  {showDebug ? "Hide" : "Show"} Debug Info
                </Button>
                <Collapse isOpen={showDebug}>
                  <Card className="mt-2 debug-panel">
                    <CardBody>
                      <h6>Raw API Response</h6>
                      <pre style={{fontSize: "11px", maxHeight: "400px", overflow: "auto"}}>
                        {JSON.stringify(result, null, 2)}
                      </pre>
                    </CardBody>
                  </Card>
                </Collapse>
              </section>
            </CardBody>
          </Card>
        </div>
      )}
    </BackgroundColorContext.Consumer>
  );
};

export default AnalysisResults;
